/**
 * MOE Indicators API Service
 * Handles communication with MOE standard indicators backend endpoints
 * Supports education level filtering, ownership filtering, and 7 indicator categories
 */

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1') + '/sis-integration';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('token') || localStorage.getItem('emis_auth_token');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Token ${token}`;
  }
  return headers;
}

// ──────────────────────────────────────────────────────────────────────────────
// Types & Interfaces
// ──────────────────────────────────────────────────────────────────────────────

export interface MOEIndicatorTemplate {
  code: string;
  name: string;
  description: string;
  category: string;
  data_type: string;
  unit: string;
  supports_level: boolean;
  supports_ownership: boolean;
  default_level: string;
  default_ownership: string;
  default_level_label: string;
  default_ownership_label: string;
  query_type: string;
  gender?: string;
  facility?: string;
}

export interface MOEIndicatorResult {
  area_name: string;
  education_level?: string;
  indicator_value: number;
  period: string;
  source: string;
  year: string;
}

export interface MOECalculationRequest {
  indicator_code: string;
  academic_year: string;
  group_by: 'district' | 'region' | 'national';
  education_level?: 'pre_primary' | 'primary' | 'secondary' | 'basic' | 'all';
  ownership?: 'government' | 'private' | 'all';
}

export interface MOECalculationResponse {
  status: string;
  data: {
    indicator_code: string;
    indicator_name: string;
    category: string;
    academic_year: string;
    education_level: string;
    education_level_label: string;
    ownership: string;
    ownership_label: string;
    group_by: string;
    unit: string;
    data_type: string;
    results: MOEIndicatorResult[];
    total_records: number;
  };
}

export interface MetaOption {
  value: string;
  label: string;
}

export interface CategoryMeta {
  value: string;
  label: string;
  icon: string;
  color: string;
}

export interface MOETemplatesResponse {
  status: string;
  data: MOEIndicatorTemplate[];
  meta: {
    education_levels: MetaOption[];
    ownership_types: MetaOption[];
    categories: CategoryMeta[];
    total_indicators: number;
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// Education Level & Ownership Constants (frontend fallback)
// ──────────────────────────────────────────────────────────────────────────────

export const EDUCATION_LEVELS: MetaOption[] = [
  { value: 'pre_primary', label: 'Pre-Primary' },
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'basic', label: 'Basic (Primary + Secondary)' },
  { value: 'all', label: 'All Levels' },
];

export const OWNERSHIP_TYPES: MetaOption[] = [
  { value: 'government', label: 'Government' },
  { value: 'private', label: 'Private' },
  { value: 'all', label: 'All (Government + Private)' },
];

export const INDICATOR_CATEGORIES: CategoryMeta[] = [
  { value: 'school_count', label: 'School Counts', icon: 'school', color: '#1976d2' },
  { value: 'enrollment', label: 'Enrollment', icon: 'people', color: '#388e3c' },
  { value: 'efficiency', label: 'Efficiency', icon: 'trending_up', color: '#f57c00' },
  { value: 'infrastructure', label: 'Infrastructure', icon: 'business', color: '#7b1fa2' },
  { value: 'staff', label: 'Teachers & Staff', icon: 'person', color: '#c62828' },
  { value: 'equity', label: 'Equity & Inclusion', icon: 'accessibility', color: '#00838f' },
  { value: 'academic', label: 'Academic Performance', icon: 'assessment', color: '#e65100' },
];

// ──────────────────────────────────────────────────────────────────────────────
// API Functions
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Get all available MOE indicator templates with metadata
 */
export const getMOEIndicatorTemplates = async (): Promise<MOETemplatesResponse> => {
  const response = await fetch(`${API_BASE_URL}/moe-indicators/templates/`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login';
    }
    throw new Error(`Failed to fetch MOE indicator templates: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Calculate values for a specific MOE indicator
 * Supports education_level and ownership filtering
 */
export const calculateMOEIndicator = async (
  request: MOECalculationRequest
): Promise<MOECalculationResponse> => {
  const response = await fetch(`${API_BASE_URL}/moe-indicators/calculate/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login';
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to calculate MOE indicator: ${response.statusText}`
    );
  }

  return response.json();
};

/**
 * Get results for a specific MOE indicator (alternative GET endpoint)
 */
export const getMOEIndicatorResults = async (
  indicatorCode: string,
  academicYear: string = '2026',
  groupBy: string = 'district',
  educationLevel?: string,
  ownership?: string
): Promise<MOECalculationResponse> => {
  const params = new URLSearchParams({
    year: academicYear,
    group_by: groupBy,
  });

  if (educationLevel) {
    params.append('education_level', educationLevel);
  }
  if (ownership) {
    params.append('ownership', ownership);
  }

  const response = await fetch(
    `${API_BASE_URL}/moe-indicators/${indicatorCode}/?${params}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    }
  );

  if (!response.ok) {
    if (response.status === 401) {
      window.location.href = '/login';
    }
    throw new Error(`Failed to get MOE indicator results: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Export MOE indicator results to CSV format
 */
export const exportMOEIndicatorResults = (
  results: MOEIndicatorResult[],
  indicatorCode: string,
  indicatorName: string,
  academicYear: string,
  educationLevel?: string,
  ownership?: string
): void => {
  if (results.length === 0) {
    throw new Error('No results to export');
  }

  const csvHeaders = [
    'Area',
    'Indicator Code',
    'Indicator Name',
    'Value',
    'Education Level',
    'Ownership',
    'Period',
    'Source',
    'Year',
  ];

  const csvRows = results.map(result => [
    result.area_name,
    indicatorCode,
    indicatorName,
    result.indicator_value.toString(),
    educationLevel || result.education_level || 'All Levels',
    ownership || 'All',
    result.period,
    result.source,
    result.year,
  ]);

  const csvContent = [csvHeaders, ...csvRows]
    .map(row => row.map(field => `"${field}"`).join(','))
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${indicatorCode}_${academicYear}_results.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

// ──────────────────────────────────────────────────────────────────────────────
// Display Helpers
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Format indicator value for display based on unit and data type
 */
export const formatIndicatorValue = (
  value: number,
  unit: string,
  dataType: string = 'integer'
): string => {
  if (dataType === 'decimal' && unit === 'percentage') {
    return `${value.toFixed(1)}%`;
  }

  if (dataType === 'decimal' && unit === 'ratio') {
    return value.toFixed(2);
  }

  if (['schools', 'students', 'teachers', 'classrooms', 'toilets'].includes(unit)) {
    return value.toLocaleString();
  }

  return value.toString();
};

/**
 * Get category color for UI display
 */
export const getCategoryColor = (category: string): string => {
  const cat = INDICATOR_CATEGORIES.find(c => c.value === category.toLowerCase());
  return cat?.color || '#666666';
};

/**
 * Get category label for display
 */
export const getCategoryLabel = (category: string): string => {
  const cat = INDICATOR_CATEGORIES.find(c => c.value === category.toLowerCase());
  return cat?.label || category;
};

/**
 * Get education level label
 */
export const getEducationLevelLabel = (level: string): string => {
  const lvl = EDUCATION_LEVELS.find(l => l.value === level);
  return lvl?.label || level;
};

/**
 * Get ownership label
 */
export const getOwnershipLabel = (ownership: string): string => {
  const own = OWNERSHIP_TYPES.find(o => o.value === ownership);
  return own?.label || ownership;
};

/**
 * Get unit label for display
 */
export const getUnitLabel = (unit: string): string => {
  const unitLabels: Record<string, string> = {
    schools: 'Schools',
    students: 'Students',
    teachers: 'Teachers',
    classrooms: 'Classrooms',
    toilets: 'Toilets',
    percentage: '%',
    ratio: 'Ratio',
  };
  return unitLabels[unit] || unit;
};

/**
 * Validate MOE calculation request
 */
export const validateMOECalculationRequest = (
  request: MOECalculationRequest
): string[] => {
  const errors: string[] = [];

  if (!request.indicator_code) {
    errors.push('Indicator code is required');
  }

  if (!request.academic_year) {
    errors.push('Academic year is required');
  }

  if (!['district', 'region', 'national'].includes(request.group_by)) {
    errors.push('Group by must be one of: district, region, national');
  }

  if (request.education_level && !['pre_primary', 'primary', 'secondary', 'basic', 'all'].includes(request.education_level)) {
    errors.push('Education level must be one of: pre_primary, primary, secondary, basic, all');
  }

  if (request.ownership && !['government', 'private', 'all'].includes(request.ownership)) {
    errors.push('Ownership must be one of: government, private, all');
  }

  return errors;
};

/**
 * Group templates by category for display
 */
export const groupTemplatesByCategory = (
  templates: MOEIndicatorTemplate[]
): Record<string, MOEIndicatorTemplate[]> => {
  const grouped: Record<string, MOEIndicatorTemplate[]> = {};

  for (const template of templates) {
    const category = template.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(template);
  }

  return grouped;
};

/**
 * Filter templates by category, education level support, and ownership support
 */
export const filterTemplates = (
  templates: MOEIndicatorTemplate[],
  categoryFilter?: string,
  levelFilter?: string,
  ownershipFilter?: string
): MOEIndicatorTemplate[] => {
  return templates.filter(template => {
    if (categoryFilter && categoryFilter !== 'all' && template.category !== categoryFilter) {
      return false;
    }
    // If a specific level is selected but template doesn't support level filtering, still show it
    // (it will use default level)
    if (levelFilter && levelFilter !== 'all' && !template.supports_level) {
      return false;
    }
    if (ownershipFilter && ownershipFilter !== 'all' && !template.supports_ownership) {
      return false;
    }
    return true;
  });
};

export default {
  getMOEIndicatorTemplates,
  calculateMOEIndicator,
  getMOEIndicatorResults,
  exportMOEIndicatorResults,
  formatIndicatorValue,
  getCategoryColor,
  getCategoryLabel,
  getEducationLevelLabel,
  getOwnershipLabel,
  getUnitLabel,
  validateMOECalculationRequest,
  groupTemplatesByCategory,
  filterTemplates,
  EDUCATION_LEVELS,
  OWNERSHIP_TYPES,
  INDICATOR_CATEGORIES,
};
